import * as React from 'react';

import { cn } from '@rematter/ui-web';
import { useAtom } from 'jotai';
import { FolderTree, Layers, Package, Tags } from 'lucide-react';

import * as state from '../../_state/inventory.state';
import { useTranslations } from '../../i18n/useTranslations';
import { InventorySection } from '../../types/inventoryItem';

import { CategoriesListView } from './categories/_CategoriesListView';
import { ItemAliasesListView } from './item-aliases/_ItemAliasesListView';
import { ItemGroupsListView } from './item-groups/_ItemGroupsListView';
import { ItemsListView } from './items/_ItemsListView';

const SECTION_IDS: { id: InventorySection; icon: React.ElementType }[] = [
  { id: 'items', icon: Package },
  { id: 'aliases', icon: Tags },
  { id: 'categories', icon: FolderTree },
  { id: 'groups', icon: Layers },
];

export const InventoryManagementView = () => {
  const t = useTranslations();
  const [currentSection, setCurrentSection] = useAtom(state.$currentSection);

  const sectionLabels: Record<InventorySection, string> = {
    items: t.inventoryView.sections.items,
    aliases: t.inventoryView.sections.itemAliases,
    categories: t.inventoryView.sections.categories,
    groups: t.inventoryView.sections.itemGroups,
  };

  const activeSection = (currentSection as InventorySection) || 'items';

  React.useEffect(() => {
    if (!currentSection) {
      setCurrentSection('items' as unknown as InventorySection);
    }
  }, [currentSection, setCurrentSection]);

  const renderPanel = () => {
    switch (activeSection) {
      case 'items':
        return <ItemsListView />;
      case 'aliases':
        return <ItemAliasesListView />;
      case 'categories':
        return <CategoriesListView />;
      case 'groups':
        return <ItemGroupsListView />;
      default:
        return <ItemsListView />;
    }
  };

  return (
    <div className="@InventoryManagementView flex h-full">
      {/* Left sidebar */}
      <div className="w-[200px] shrink-0 border-r border-solid border-neutral/10 bg-white">
        <div className="border-b border-solid border-neutral/10 px-3 py-2 text-xs font-medium text-neutral/40">
          {t.inventoryView.sidebar.title}
        </div>
        {SECTION_IDS.map((section) => {
          const isSelected = section.id === activeSection;
          const Icon = section.icon;

          return (
            <button
              key={section.id}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-2 text-left text-sm outline-none hover:bg-neutral/10',
                isSelected && 'bg-neutral/60 text-white hover:bg-neutral/60',
              )}
              onClick={(e) => {
                e.preventDefault();
                setCurrentSection(section.id as unknown as InventorySection);
              }}
            >
              <Icon className="size-4" />
              <span>{sectionLabels[section.id]}</span>
            </button>
          );
        })}
      </div>

      {/* Main content area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {renderPanel()}
      </div>
    </div>
  );
};
